// 계정·서버 저장 공용 약속(D5a, docs/ACCOUNT-SAVE-PRD.md): 클라이언트(src/account)와 Worker(src/server)가 같이 쓴다.
// 순수 상수·타입만 둔다(DOM·저장소·게임 모듈 import 없음 — Worker 번들을 가볍게). 게임 상수와 같은지는 protocol.test.ts가 확인한다.

/** 세션 쿠키 이름. __Host- 접두사: Secure·Path=/·Domain 없음이 강제돼 하위 도메인이 덮어쓸 수 없다 */
export const SESSION_COOKIE = '__Host-dh_sid';
/** 세션 쿠키 수명(초). 크롬 상한 400일. 부팅마다 session 호출이 다시 늘린다 */
export const SESSION_MAX_AGE_S = 400 * 86400;
/** 계정당 세션(기기·브라우저) 최대 수. 넘으면 가장 오래 안 쓴 것부터 폐기 */
export const SESSIONS_PER_ACCOUNT = 10;

/** 스냅샷 최대 크기(UTF-8 바이트, JSON 직렬화 기준) */
export const MAX_SNAPSHOT_BYTES = 256 * 1024;
/** 요청 본문 최대 크기 */
export const MAX_BODY_BYTES = 300 * 1024;
/** 서버가 남기는 이전 스냅샷 수(계정당) */
export const HISTORY_KEEP = 5;

/** 서버 상한 검사(§5.2): 게임 상수와 같아야 한다(protocol.test.ts) */
export const LIMITS = {
  /** 마지막 스테이지 번호(stages.ts LAST_STAGE_ORDINAL) */
  lastStage: 60,
  /** 장비 강화 상한(world.ts ENHANCE_MAX) */
  enhanceMax: 15,
  /** 숙련 최대 레벨 = 배분 포인트 합 상한(shared/mastery MASTERY.max, C4) */
  masteryMax: 300,
  /** 숙련 한 능력치 포인트 상한(shared/mastery MASTERY_STAT_CAP) */
  masteryStatCap: 75,
  /** 부위 초월 단계 상한(shared/mastery TRANSCEND_MAX = +20) */
  transcendMax: 5,
  /** 골드 상한(정상 플레이로 닿을 수 없는 값 — 안전 정수 범위 안) */
  goldMax: 1_000_000_000_000,
} as const;

/** 빈도 제한(창 길이 ms, 횟수) */
export const RATE = {
  guestPerIp: { windowMs: 3_600_000, max: 20 },
  googlePerIp: { windowMs: 3_600_000, max: 20 },
  savePut: { windowMs: 600_000, max: 30 },
  saveGet: { windowMs: 600_000, max: 120 },
  deletePerAccount: { windowMs: 3_600_000, max: 5 },
  /** 캐릭터 생성·이름 변경·삭제·복구(계정당) — 태그 발급 남용 방지 */
  characterWrite: { windowMs: 3_600_000, max: 30 },
} as const;

/** 정식·별칭 호스트(쓰기 요청 Origin 허용 목록). workers.dev 진단 주소는 넣지 않는다 */
export const ALLOWED_ORIGINS: readonly string[] = ['https://dghero.tllhouse.com', 'https://dungeonhero.tllhouse.com'];

/** Google 웹 OAuth 클라이언트 ID(공개 값 — blade-surge와 같은 클라이언트). 서버는 env.GOOGLE_CLIENT_IDS를 쓴다 */
export const GOOGLE_WEB_CLIENT_ID = '898058209232-qr2isup4rafe3fhhgt5e1vvntu267sf3.apps.googleusercontent.com';
/** 개발 전용 테스트 신원 접두사(DEV_FAKE_GOOGLE=1 + localhost에서만 서버가 받는다) */
export const DEV_TOKEN_PREFIX = 'dev:';

export type Provider = 'guest' | 'google';
/**
 * 신원 연결 제공자(links.provider — 제공자 일반형 표). 지금은 google만 붙어 있고 chatgpt(「ChatGPT로 로그인」, CHARACTER-PRD §7)·kakao는 자리다.
 * 로그인(신원)과 AI 사용 권한은 따로 저장한다: AI 권한은 ai_grants 표(src/server/schema.ts SCHEMA_CHAR)에 동의한 범위·시각만.
 */
export type LinkProvider = 'google' | 'chatgpt' | 'kakao';

/** 진행 점수(사전식 비교: xp → stages → gold → receipts) */
export interface ProgressScore { xp: number; stages: number; gold: number; receipts: number }

/** 스냅샷: 진행 키 원문 묶음 */
/**
 * 진행 스냅샷. g = 쓴 빌드가 아는 진행 키 세대(account/keys.ts SYNC_GEN). 없으면 0(세대 도입 전 빌드).
 * 적용·서버 저장은 쓴 빌드가 모르는 세대의 키를 '없음 = 지움'으로 해석하지 않는다(L1029 스냅샷 키 유실 방지)
 */
export interface Snapshot { v: 1; keys: Record<string, string>; g?: number }

export interface AccountView { id: string; provider: Provider; createdAt: number }
export interface SaveMeta { rev: number; updatedAt: number; score: ProgressScore | null }
export interface SessionResponse { account: AccountView; save: SaveMeta }
export interface SaveResponse extends SaveMeta { data?: Snapshot }
/**
 * clamped = 서버 상한을 넘어 이전 값으로 보관한 키. merged = 받아들였지만 서버가 이전 값과 병합해 저장한 키의 저장본(보낸 원문과 다를 때만, 지금은 도감 키 하나).
 * 올린 기기가 저장본을 로컬에 합쳐야 다른 기기의 진행을 모르고 이어 가지 않는다(sync.ts adoptMerged)
 */
export interface PutResponse extends SaveMeta { clamped: string[]; merged?: Record<string, string> }
export interface ConflictResponse extends SaveMeta { error: 'conflict' }
export interface GoogleResponse { result: 'linked' | 'switched' | 'already'; account: AccountView; save: SaveMeta }

/** 서버 오류 코드(본문 {error}) */
export type ErrorCode =
  | 'bad-origin' | 'bad-content-type' | 'bad-json' | 'too-large' | 'bad-request' | 'not-found'
  | 'no-session' | 'conflict' | 'bad-snapshot' | 'rate' | 'bad-token' | 'token-expired'
  | 'link-unavailable' | 'link-disabled' | 'already-linked' | 'server' | 'misconfigured'
  // 인앱 결제(src/server/iap.ts, docs/IAP-PRD.md §6)
  | 'iap-disabled' | 'iap-test-restricted' | 'iap-monthly-limit' | 'iap-notice-required' | 'app-channel' | 'link-required' | 'product-unavailable' | 'insufficient' | 'payment-mismatch' | 'payment-pending'
  | 'payment-failed' | 'payment-cancelled' | 'portone-unavailable' | 'bad-signature' | 'not-eligible'
  | 'iap-once' | 'play-unavailable' | 'token-used'
  // 캐릭터(src/server/characters.ts, docs/CHARACTER-PRD.md §13)
  | 'bad-name' | 'name-full' | 'slots-full' | 'id-taken' | 'rename-locked' | 'character-deleted' | 'not-restorable' | 'migration-lost' | 'migration-busy'
  // MMO 공유 필드 운영 스위치(src/server/worker.ts FIELD_NET)
  | 'field-disabled'
  // 파티 던전·우편함·거래소(src/server/party.ts·mail.ts·market.ts, docs/PARTY-TRADE-PRD.md)
  | 'party-disabled' | 'raid-disabled' | 'mail-disabled' | 'market-disabled'
  | 'item-invalid' | 'not-tradable' | 'item-owned' | 'item-modified' | 'deposit-closed' | 'deposit-pending' | 'price-range' | 'limit' | 'own-listing' | 'closed' | 'bid-low' | 'use-buyout' | 'outbid' | 'no-buyout' | 'has-bids';
